import { Link, Navigate } from 'react-router-dom';
import { Scoreboard } from '../components/Scoreboard';
import { CardHand } from '../components/cards/CardHand';
import { RoomCodeBadge, RoundStatus } from '../components/game/Badges';
import { GameMenu } from '../components/game/GameMenu';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { MaterialIcon } from '../components/MaterialIcon';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { ConnectionStatusBadge } from '../components/ConnectionStatusBadge';
import { useGame } from '../state/gameContext';
import { submitButtonLabel, submitDisabledReason } from '../state/cardSelection';
import { playerToScoreboard } from '../state/mockGameReducer';
import { playSound } from '../state/soundManager';

export default function GameTablePage() {
  const {
    state,
    currentPlayer,
    toggleCard,
    clearSelection,
    submitAnswer,
    runtimeMode,
    connectionStatus,
  } = useGame();

  if (!state.roomCode || state.phase === 'none' || state.phase === 'lobby') {
    return <Navigate to="/lobby" replace />;
  }

  const scoreboardPlayers = state.players.map((p) => playerToScoreboard(p, state.judgeId));

  const pick = state.blackCardPick;
  const selectedCount = state.selectedCardIds.length;
  const isJudge = currentPlayer?.id === state.judgeId;
  const viewerHasSubmitted = state.viewerHasSubmitted ?? false;
  const maySubmit = state.canSubmit ?? true;

  const disabledReason = submitDisabledReason({
    pick,
    selectedCount,
    maySubmit,
    viewerHasSubmitted,
    isJudge,
  });
  const canSubmit = disabledReason === null && selectedCount === pick;
  const submitLabel = submitButtonLabel(selectedCount, pick);

  const roundStatusText = viewerHasSubmitted
    ? 'Answer submitted — waiting for other players…'
    : isJudge
      ? 'You are judging this round — pick a winner after everyone submits'
      : pick > 1
        ? `Choose ${pick} cards for this prompt`
        : 'Choose your best answer';

  // A late joiner sits out the current round (server marks them joinedMidRound).
  const sittingOut = (state.joinedMidRound ?? false) && !isJudge && !viewerHasSubmitted;
  const showHand = !isJudge && !viewerHasSubmitted && !sittingOut;

  const handleToggle = (id: string) => {
    playSound('select');
    toggleCard(id);
  };
  const handleSubmit = () => {
    playSound('submit');
    submitAnswer();
  };

  return (
    <div className="play-screen bg-background text-on-surface h-[100dvh] overflow-hidden flex flex-col">
      <header className="flex justify-between items-center px-margin-mobile h-16 shrink-0 bg-surface/90 border-b border-white/10 shadow-sm z-50">
        <div className="flex items-center gap-3 min-w-0">
          <Link
            to="/"
            className="font-headline-md-mobile text-headline-md-mobile font-black text-primary italic tracking-tighter shrink-0 hidden sm:block"
          >
            Deck After Dark
          </Link>
          <RoomCodeBadge code={state.roomCode} round={`Round ${state.roundNumber}`} />
          <span
            className="text-label-sm text-on-surface-variant font-label-bold"
            data-testid="pick-count"
            data-pick={pick}
          >
            Pick {pick}
          </span>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          {runtimeMode === 'server' && (
            <ConnectionStatusBadge status={connectionStatus} className="hidden sm:inline-flex" />
          )}
          <GameMenu showChat showLeave compact />
          {currentPlayer && (
            <PlayerAvatar
              initials={currentPlayer.initials}
              gradient={currentPlayer.gradient}
              size="sm"
              alt={`${currentPlayer.name} avatar`}
              className="border border-primary/30"
            />
          )}
        </div>
      </header>

      <Scoreboard players={scoreboardPlayers} />

      <main className="flex-1 min-h-0 flex flex-col lg:pl-72">
        {/* Play area: status + black card. Status sits ABOVE the card so it is
            never obstructed (fixes the top-text overlap finding). */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center gap-3 px-4 py-3">
          <div className="shrink-0 flex flex-col items-center gap-1 text-center max-w-md">
            <RoundStatus text={roundStatusText} />
            {disabledReason && !viewerHasSubmitted && !isJudge && (
              <p className="text-label-sm text-on-surface-variant">{disabledReason}</p>
            )}
          </div>

          <div
            data-testid="black-card"
            className="shrink-0 w-full max-w-2xl bg-[#1f2822] rounded-lg p-5 md:p-6 border border-[#485044] flex flex-col gap-4"
          >
            <div className="font-card-text text-[clamp(1rem,2.4vh,1.6rem)] leading-snug text-white whitespace-pre-wrap break-words">
              {state.blackCardText}
            </div>
            <div className="flex justify-between items-end shrink-0 pt-2">
              <div className="flex flex-col">
                <span className="font-headline-md text-primary italic text-base">D A D</span>
                <span className="text-[10px] text-on-surface-variant/40">Deck After Dark</span>
              </div>
              <MaterialIcon name="playing_cards" className="text-on-surface-variant/40" filled />
            </div>
          </div>
        </div>

        {/* Hand dock: deterministic height, no heavy backdrop blur. */}
        <section className="shrink-0 bg-surface-container-highest/95 border-t border-white/10 pb-20 md:pb-3 pt-3">
          {showHand ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-on-surface-variant px-4 text-center" aria-live="polite">
                {selectedCount} / {pick} selected · {pick > 1 ? 'Cards are read in selection order. Tap again to remove.' : 'Tap a card to select it. Submit when ready.'}
              </p>
              <div className="flex items-center gap-2 md:gap-4">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  title={disabledReason ?? undefined}
                  data-testid="submit-answer"
                  className="px-6 md:px-8 py-2.5 bg-primary text-on-primary font-bold rounded-full shadow-lg shadow-primary/20 active:scale-95 transition-all hover:brightness-110 text-sm md:text-base disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitLabel}
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={selectedCount === 0}
                  data-testid="clear-selection"
                  className="px-4 md:px-6 py-2.5 bg-surface-container-high text-on-surface-variant font-bold rounded-full border border-white/10 active:scale-95 transition-all hover:bg-surface-variant text-sm md:text-base disabled:opacity-40"
                >
                  Clear
                </button>
              </div>
              <CardHand
                cards={state.hand}
                selectedIds={state.selectedCardIds}
                onToggle={handleToggle}
                showWild={state.settings.wildCardsEnabled}
              />
            </div>
          ) : (
            <div
              className="text-center px-4 py-6"
              data-testid={
                isJudge ? 'judge-waiting' : sittingOut ? 'late-join-waiting' : 'submitted-waiting'
              }
            >
              <p className="text-label-sm text-on-surface-variant">
                {isJudge
                  ? 'Your hand is hidden while you judge. Submissions appear when everyone is in.'
                  : sittingOut
                    ? 'You joined mid-round — you’ll play from the next round.'
                    : 'Sit tight — the judge will reveal answers soon.'}
              </p>
            </div>
          )}
        </section>
      </main>

      <MobileBottomNav active="/game" />
    </div>
  );
}
